
import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { MfaGate } from "./MfaGate";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { session, loading, signOut } = useAuth();
  const queryClient = useQueryClient();
  const location = useLocation();
  // Start as TRUE to block rendering until we confirm user is NOT archived
  const [checkingArchived, setCheckingArchived] = useState(true);
  const [isArchived, setIsArchived] = useState(false);
  const [role, setRole] = useState<string | null>(null);

  // Layer 2: Secondary check for archived users as backup
  useEffect(() => {
    const checkArchivedStatus = async () => {
      // No session = no need to check, allow access
      if (!session?.user?.id) {
        setCheckingArchived(false);
        return;
      }
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('archived')
          .eq('user_id', session.user.id)
          .maybeSingle();
        
        if (error) {
          console.error('🔐 [ProtectedRoute] Error checking archived status:', error);
          // Fail open on error - allow access but log it
          setCheckingArchived(false);
          return;
        }
        
        // Check if profile doesn't exist (permanently deleted user)
        if (data === null) {
          console.log('🚫 [ProtectedRoute] Profile not found (account removed), signing out');
          toast.error("Your account has been removed. Please contact support if you believe this is an error.");
          setIsArchived(true);
          queryClient.clear();
          await signOut();
          return;
        }
        
        if (data?.archived === true) {
          console.log('🚫 [ProtectedRoute] User is archived, signing out');
          setIsArchived(true);
          // CRITICAL: Clear ALL cached data before signing out archived user
          queryClient.clear();
          await signOut();
          // Don't set checkingArchived to false - redirect will happen
          return;
        }
        
        // User is NOT archived - allow access
        try {
          const { data: r } = await supabase.rpc('get_user_role', { user_id: session.user.id });
          setRole((r as string) ?? null);
        } catch (e) {
          console.error('🔐 [ProtectedRoute] Error fetching role:', e);
        }
        setCheckingArchived(false);
      } catch (e) {
        console.error('🔐 [ProtectedRoute] Exception checking archived:', e);
        // Fail open on exception
        setCheckingArchived(false);
      }
    };

    checkArchivedStatus();
  }, [session?.user?.id, signOut, queryClient]);

  if (loading || checkingArchived) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!session || isArchived) {
    return <Navigate to="/login" replace />;
  }

  // Production-role users are limited to the Assembly and Quality Control pages.
  if (role === 'production') {
    const allowed = ['/dashboard/assembly', '/dashboard/production'];
    if (!allowed.includes(location.pathname)) {
      return <Navigate to="/dashboard/assembly" replace />;
    }
  }

  return <MfaGate>{children}</MfaGate>;
};
